/**
 * Tenant Health API Client
 *
 * Phase 3.5-B: Multi-Tenant Visibility
 */

import type {
  TenantHealthMatrixResponse,
  IsolationDistData,
  VersionDistData,
} from '../types/tenantHealth';

const API_BASE = '/api/admin/dashboard';

type AuthFetch = (url: string, options?: RequestInit) => Promise<Response>;

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Request failed (${response.status})`);
  }
  return response.json();
}

export interface FetchHealthMatrixParams {
  status?: string[];
  isolationType?: string[];
  search?: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
}

export const fetchTenantHealthMatrix = async (
  authFetch: AuthFetch,
  params: FetchHealthMatrixParams = {}
): Promise<TenantHealthMatrixResponse> => {
  const searchParams = new URLSearchParams();

  if (params.status) {
    params.status.forEach((s) => searchParams.append('status[]', s));
  }
  if (params.isolationType) {
    params.isolationType.forEach((t) => searchParams.append('isolationType[]', t));
  }
  if (params.search) searchParams.set('search', params.search);
  if (params.page) searchParams.set('page', String(params.page));
  if (params.pageSize) searchParams.set('pageSize', String(params.pageSize));
  if (params.sortBy) searchParams.set('sortBy', params.sortBy);
  if (params.sortDirection) searchParams.set('sortDirection', params.sortDirection);

  const qs = searchParams.toString();
  const url = `${API_BASE}/tenants/health-matrix${qs ? `?${qs}` : ''}`;
  const response = await authFetch(url);
  return handleResponse<TenantHealthMatrixResponse>(response);
};

export const fetchIsolationDistribution = async (
  authFetch: AuthFetch
): Promise<IsolationDistData> => {
  const response = await authFetch(`${API_BASE}/distribution/isolation`);
  return handleResponse<IsolationDistData>(response);
};

export const fetchVersionDistribution = async (
  authFetch: AuthFetch
): Promise<VersionDistData> => {
  const response = await authFetch(`${API_BASE}/distribution/versions`);
  return handleResponse<VersionDistData>(response);
};
